// The owner's view of the agents (the owner-agents prototype, "AI agents"): the company kill switch,
// this month's spend against the cap, each agent with its switch, what it did today (automatic,
// asked, refused), its budget and what waits for a person, then the day's activity with each run's
// steps, and the recent runs (RecentRuns.tsx). Switches, budgets and a run's cost are the owner's
// (agents.manage); the manager reads.

import { Fragment, useState } from 'react';
import type { Approval } from '../books/types.ts';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { Empty, errorText, Panel, useAct, useQuery } from '../books/ui.tsx';
import { Loading } from '../shared/Loading.tsx';
import { Lock, agentKeyOf } from '../shared/ApprovalsInbox.tsx';
import {
  stepChip,
  actionWords,
  activityInput,
  budgetLevel,
  clockParts,
  initials,
  roleLabel,
  taskText,
  type Mode,
} from './model.ts';
import type { AgentsProps } from './props.ts';
import { RecentRuns } from './RecentRuns.tsx';
import { RunPanel } from './RunPanel.tsx';
import { useDesign } from '../blueprint/design.ts';
import { type ActivityPage, type ActivityStep, type AgentSummary, type AgentsList } from './types.ts';
import { usePaged } from '../blueprint/paged.ts';
import { shownText } from '../shared/pages.ts';

/** "10:05" large and "a.m." small, as the board writes a time. */
function TimeOf({ at, timeZone }: { at: string; timeZone?: string | undefined }) {
  const t = clockParts(at, timeZone);
  return (
    <>
      {t.time}
      {/* a real space, so it reads "10:30 a.m." aloud and when copied */}
      <small> {t.suffix}</small>
    </>
  );
}

function Meter({ share, level }: { share: number; level: string }) {
  return (
    <div className={`meter ${level}`} aria-hidden="true">
      <i style={{ width: `${Math.round(share * 100)}%` }} />
    </div>
  );
}

function AgentCard({
  a,
  waiting,
  props,
  companyStopped,
  onSwitch,
  busy,
}: {
  a: AgentSummary;
  waiting: number;
  props: AgentsProps;
  companyStopped: boolean;
  onSwitch: (a: AgentSummary) => void;
  busy: boolean;
}) {
  const b = budgetLevel(a.spentCents, a.budgetCents);
  const ownSwitchOn = !a.switchedOff;
  return (
    <li className={`stone agent${a.on ? '' : ' off'}`}>
      <div className="agent-head">
        <span className={`agav${a.on ? '' : ' off'}`} aria-hidden="true">
          {initials(a.name)}
        </span>
        <div className="grow">
          <strong>{a.name}</strong>
          <span className="muted small clamp" title={a.runs}>
            Role: {roleLabel(a.role)} · {a.runs}
          </span>
        </div>
        <button
          type="button"
          className="switch"
          role="switch"
          aria-checked={ownSwitchOn}
          aria-label={`${a.name} on or off`}
          disabled={!props.isOwner || companyStopped || busy}
          title={!props.isOwner ? 'Only the owner turns agents on or off' : undefined}
          onClick={() => onSwitch(a)}
        />
      </div>
      <div className="chips">
        <span className="chip say ok">{a.today.automatic} automatic</span>
        <span className="chip say warn">{a.today.asked} asked</span>
        {a.today.refused > 0 && <span className="chip say bad">{a.today.refused} refused</span>}
        {waiting > 0 && (
          <button type="button" className="linkish" onClick={() => props.go('approvals')}>
            {waiting} waiting →
          </button>
        )}
      </div>
      <div className="budget-line">
        <span className="small">Budget this month</span>
        <b className="small">{b.text}</b>
      </div>
      <Meter share={b.share} level={b.level} />
      {b.level === 'warn' && <span className="chip say warn">{Math.round(b.share * 100)} % used</span>}
      <div className="budget-line">
        <span className="muted small">
          {a.runsThisMonth} {a.runsThisMonth === 1 ? 'run' : 'runs'} this month
        </span>
        {a.on ? (
          <span className="chip say ok">On</span>
        ) : (
          <span className="chip say bad">
            Stopped: {a.stoppedReason?.replace(/\.$/, '')}
            {a.key === 'receptionist' ? ' · calls ring the office' : ''}
          </span>
        )}
      </div>
      <button type="button" className="btn soft" onClick={() => props.go('settings', a.key)}>
        Settings
      </button>
    </li>
  );
}

const FILTERS: [Mode | '', string][] = [
  ['', 'All'],
  ['automatic', 'Automatic'],
  ['asked', 'Asked'],
  ['refused', 'Refused'],
];

function Activity({ props, names }: { props: AgentsProps; names: Map<string, string> }) {
  const [mode, setMode] = useState<Mode | ''>('');
  const [agentKey, setAgentKey] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  // the mode and agent filtered by the server, a page at a time (list caps M16): a busy day passes a page
  const day = usePaged<ActivityStep, ActivityPage>(
    'agents.activity',
    activityInput(agentKey, mode),
    (p) => p.steps,
  );
  const rows = day.rows ?? [];
  const total = day.first?.total ?? rows.length;
  return (
    <Panel
      title="Activity today"
      aside={
        <div className="row-actions">
          <div className="seg" role="group" aria-label="Filter activity">
            {FILTERS.map(([m, label]) => (
              <button
                key={m || 'all'}
                className={`btn quiet${mode === m ? ' on' : ''}`}
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="sr" htmlFor="activity-agent">
            Agent
          </label>
          <select
            id="activity-agent"
            className="field"
            value={agentKey}
            onChange={(e) => setAgentKey(e.target.value)}
          >
            <option value="">Every agent</option>
            {[...names].map(([k, n]) => (
              <option key={k} value={k}>
                {n}
              </option>
            ))}
          </select>
        </div>
      }
    >
      {!day.rows ? (
        <Loading error={day.error} what="today’s activity" kind="table" retry={day.reload} />
      ) : rows.length === 0 ? (
        <Empty>Nothing yet today.</Empty>
      ) : (
        <ul className="rows log">
          {rows.map((s) => {
            const id = `${s.runId}:${s.n}`;
            const [cls, label] = stepChip(s);
            return (
              <Fragment key={id}>
                <li className="log-row">
                  <span className="time">
                    <TimeOf at={s.at} timeZone={props.timeZone} />
                  </span>
                  <span className="agav" aria-hidden="true">
                    {initials(names.get(s.agentKey) ?? s.agentKey)}
                  </span>
                  <div className="log-main">
                    <div>
                      <b>{names.get(s.agentKey) ?? s.agentKey}</b>
                    </div>
                    <span className="small">
                      <span title={s.action ?? undefined}>{actionWords(s.action, s.actionTitle)}</span> ·{' '}
                      {taskText(s.task)}
                    </span>
                    {s.decision?.note && (
                      <span className="small muted"> Note to the agent: “{s.decision.note}”</span>
                    )}
                  </div>
                  <span className={`chip say ${cls}`}>{label}</span>
                  <button
                    type="button"
                    className="btn quiet"
                    aria-expanded={open === id}
                    onClick={() => setOpen(open === id ? null : id)}
                  >
                    {open === id ? 'Hide run' : 'Open run'}
                  </button>
                </li>
                {open === id && (
                  <li className="log-detail">
                    <RunPanel runId={s.runId} timeZone={props.timeZone} showCost={props.isOwner} />
                  </li>
                )}
              </Fragment>
            );
          })}
        </ul>
      )}
      {(day.more || day.busy) && (
        <p className="row-actions">
          <span className="muted small">{shownText(rows.length, total)}</span>
          <button type="button" className="btn soft" disabled={day.busy} onClick={() => day.more?.()}>
            {day.busy ? 'Loading…' : 'Load more'}
          </button>
        </p>
      )}
    </Panel>
  );
}

/** A step-up on its own (/me/2fa/step-up): the next ten minutes count as a fresh second factor. */
async function stepUpWith(code: string): Promise<{ ok: true } | { ok: false; message: string }> {
  const csrf = (await client.session())?.csrf;
  const res = await fetch('/me/2fa/step-up', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(csrf ? { 'x-flow-csrf': csrf } : {}) },
    body: JSON.stringify({ code: code.trim() }),
  });
  const body = (await res.json().catch(() => null)) as { ok?: boolean; error?: { message: string } } | null;
  return res.ok && body?.ok
    ? { ok: true }
    : { ok: false, message: body?.error?.message ?? 'That code did not work.' };
}

export function Overview(props: AgentsProps) {
  const list = useQuery<AgentsList>('agents.list', {});
  const pending = useQuery<{ approvals: Approval[] }>('approvals.list', { status: 'pending' });
  const [armed, setArmed] = useState(false);
  // Restarting after the kill switch asks for a fresh two-factor code first (QA-099).
  const [stepCode, setStepCode] = useState<string | null>(null);
  // An owner with no two-factor of their own turns it on before restarting (QA-300): where to.
  const [ownOff, setOwnOff] = useState<string | null>(null);
  // Blueprint draws the kill switch as its danger button, outlined in red
  const bp = useDesign() === 'blueprint';
  const act = useAct(props.toast, async () => {
    await list.reload();
  });
  if (!list.data) return <Loading error={list.error} what="the agents" retry={() => void list.reload()} />;
  const { agents, company } = list.data;
  const names = new Map(agents.map((a) => [a.key, a.name]));
  const waitingFor = (key: string) =>
    pending.data?.approvals.filter((a) => agentKeyOf(a.agent) === key).length ?? 0;
  const cap = budgetLevel(company.spentCents, company.budgetCents);
  const month = new Intl.DateTimeFormat('en-CA', { month: 'long' }).format(new Date());

  const stopAll = async () => {
    if (!company.stopped && !armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    if (company.stopped) {
      // The owner's fresh second factor: straight on if they passed one in the last 10 minutes.
      try {
        await client.call('agents.setStopped', { agentKey: '*', stopped: false });
        props.toast('Agents are back on.');
        await list.reload();
      } catch (e) {
        const detail = (e as { detail?: { reason?: string; setUp?: string } }).detail;
        if (e instanceof RpcError && detail?.reason === 'step_up_required') setStepCode('');
        else if (e instanceof RpcError && detail?.reason === 'own_two_factor_off')
          setOwnOff(detail.setUp ?? '/');
        else props.toast(errorText(e));
      }
      return;
    }
    await act.run('agents.setStopped', { agentKey: '*', stopped: true }, 'Every agent is stopped.');
  };
  const confirmRestart = async () => {
    const res = await stepUpWith(stepCode ?? '');
    if (!res.ok) return props.toast(res.message);
    setStepCode(null);
    await act.run('agents.setStopped', { agentKey: '*', stopped: false }, 'Agents are back on.');
  };
  const flip = (a: AgentSummary) => {
    const turningOff = !a.switchedOff;
    void act.run(
      'agents.setStopped',
      { agentKey: a.key, stopped: turningOff },
      turningOff ? `${a.name} is off.` : `${a.name} is back on.`,
    );
  };

  return (
    <div className="books-page">
      <div className="page-head">
        <h2 className="page-title">AI agents</h2>
        <p className="muted">
          Each agent works under a role and can only use that role’s actions. Every tool call goes through the
          same checks as a person’s, and is audited.
        </p>
      </div>
      <div className={`killall${company.stopped ? ' stopped' : ''}`}>
        <div>
          <b>{company.stopped ? 'All agents are stopped.' : 'Company kill switch'}</b>
          <p className="small">
            {company.stopped
              ? 'No agent can call a model or a tool, and no outside AI tool (connected with someone’s sign-in) can act. The phone rings the office line, then voicemail.'
              : 'Stops every agent before its next model or tool call, and outside AI tools connected with someone’s sign-in too. Requests waiting for approval can’t run until you turn them back on.'}
          </p>
        </div>
        {props.isOwner ? (
          <div className="row-actions">
            <button
              type="button"
              className={`btn kill${bp ? ' danger' : ''}`}
              disabled={act.busy}
              onClick={() => void stopAll()}
            >
              {company.stopped ? 'Turn agents back on' : armed ? 'Tap again to stop all' : 'Stop all agents'}
            </button>
            {armed && (
              <button type="button" className="btn quiet" onClick={() => setArmed(false)}>
                Cancel
              </button>
            )}
            {ownOff !== null && (
              <p className="small" role="status">
                Turn on your own two-factor first, then come back to do this. <a href={ownOff}>Set it up</a>
              </p>
            )}
            {stepCode !== null && (
              <form
                className="stepup"
                onSubmit={(e) => {
                  e.preventDefault();
                  void confirmRestart();
                }}
              >
                <label>
                  Code from your authenticator app
                  <input
                    className="field"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={stepCode}
                    onChange={(e) => setStepCode(e.target.value)}
                  />
                </label>
                <button className="btn primary" type="submit" disabled={act.busy || !stepCode.trim()}>
                  Confirm and turn back on
                </button>
              </form>
            )}
          </div>
        ) : (
          <span className="lockwhy small">
            <Lock /> Only the owner stops the agents.
          </span>
        )}
      </div>
      <section className="sheet panel" aria-label={`${month} spend`}>
        <div className="panel-head">
          <h2>{month} spend</h2>
          <b>{cap.text}</b>
        </div>
        {company.budgetCents !== null && <Meter share={cap.share} level={cap.level} />}
        <p className="muted small">
          At 100 % of a budget the agent stops before its next model call (the receptionist leaves calls to
          the office line and voicemail).{' '}
          {company.budgetCents === null && 'The company has no monthly cap; set one in Settings.'}
        </p>
      </section>
      <ul className="agent-grid">
        {agents.map((a) => (
          <AgentCard
            key={a.key}
            a={a}
            waiting={waitingFor(a.key)}
            props={props}
            companyStopped={company.stopped}
            onSwitch={flip}
            busy={act.busy}
          />
        ))}
      </ul>
      <Activity props={props} names={names} />
      <RecentRuns props={props} names={names} />
    </div>
  );
}
